import React, { useState, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Delete as DeleteIcon } from '@material-ui/icons';
import FileUpload from '../../components/FileUpload';
import {
  uploadMedicalRecords,
  getMedicalRecords,
  deleteMedicalRecord,
  MedicalRecord,
} from '../../services/medicalRecordsService';
import useSnackbar from 'hooks/useSnackbar';
import { Severity } from 'types';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  title: {
    marginBottom: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  formControl: {
    marginBottom: theme.spacing(2),
    width: '100%',
  },
  submitButton: {
    marginTop: theme.spacing(2),
  },
  loadingWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: theme.spacing(1),
  },
  recordsList: {
    marginTop: theme.spacing(3),
  },
  recordItem: {
    marginBottom: theme.spacing(1),
  },
  fileName: {
    wordBreak: 'break-all',
  },
  fileLink: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

export const MedicalRecordsPage: React.FC = () => {
  const classes = useStyles();
  const showToast = useSnackbar();

  const [caseId, setCaseId] = useState<string>('');
  const [files, setFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [medicalRecords, setMedicalRecords] = useState<MedicalRecord[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Handle case ID input change
  const handleCaseIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCaseId(event.target.value);
  };

  // Handle file selection
  const handleFilesChange = (newFiles: File[]) => {
    setFiles(newFiles);
  };

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!caseId) {
      setError('Please enter a case ID');
      return;
    }

    if (files.length === 0) {
      setError('Please select at least one file to upload');
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      const response = await uploadMedicalRecords(files, caseId);
      showToast('Medical records uploaded successfully', Severity.success);

      // Reset form
      setFiles([]);

      // Refresh the list of medical records
      fetchMedicalRecords();
    } catch (error) {
      console.error('Error uploading medical records:', error);
      setError('Failed to upload medical records. Please try again.');
      showToast('Failed to upload medical records', Severity.error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch medical records for the current case ID
  const fetchMedicalRecords = useCallback(async () => {
    if (!caseId) {
      setMedicalRecords([]);
      return;
    }

    setIsSearching(true);

    try {
      const response = await getMedicalRecords(caseId);
      setMedicalRecords(response.medicalRecords);
    } catch (error) {
      console.error('Error fetching medical records:', error);
      showToast('Failed to fetch medical records', Severity.error);
    } finally {
      setIsSearching(false);
    }
  }, [caseId, showToast]);

  // Handle search button click
  const handleSearch = () => {
    if (!caseId) {
      setError('Please enter a case ID');
      return;
    }

    setError(null);
    fetchMedicalRecords();
  };

  // Handle delete medical record
  const handleDeleteRecord = async (id: number) => {
    try {
      await deleteMedicalRecord(id);
      showToast('Medical record deleted successfully', Severity.success);

      // Refresh the list of medical records
      fetchMedicalRecords();
    } catch (error) {
      console.error('Error deleting medical record:', error);
      showToast('Failed to delete medical record', Severity.error);
    }
  };

  return (
    <Container className={classes.root}>
      <Paper className={classes.paper}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Case ID"
                variant="outlined"
                fullWidth
                value={caseId}
                onChange={handleCaseIdChange}
                className={classes.formControl}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSearch}
                disabled={!caseId || isSearching}
                className={classes.formControl}
              >
                {isSearching ? (
                  <>
                    Searching
                    <div className={classes.loadingWrapper}>
                      <CircularProgress size={20} />
                    </div>
                  </>
                ) : (
                  'Search Records'
                )}
              </Button>
            </Grid>
          </Grid>

          <FileUpload
            onFilesChange={handleFilesChange}
            maxFiles={10}
            maxSize={100 * 1024 * 1024} // 100MB
          />

          {error && (
            <Alert severity="error" style={{ marginTop: 16 }}>
              {error}
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={isLoading || files.length === 0 || !caseId}
            className={classes.submitButton}
          >
            {isLoading ? (
              <>
                Uploading
                <div className={classes.loadingWrapper}>
                  <CircularProgress size={20} />
                </div>
              </>
            ) : (
              'Upload Medical Records'
            )}
          </Button>
        </form>
      </Paper>

      {medicalRecords.length > 0 && (
        <Paper className={classes.paper}>
          <Typography variant="h6" gutterBottom>
            Uploaded Medical Records
          </Typography>
          <List>
            {medicalRecords.map((record) => (
              <React.Fragment key={record.id}>
                <ListItem className={classes.recordItem}>
                  <ListItemText
                    primary={
                      <Typography variant="body1" className={classes.fileName}>
                        <a
                          href={record.docUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={classes.fileLink}
                        >
                          {record.fileName}
                        </a>
                      </Typography>
                    }
                    secondary={`Uploaded on ${new Date(
                      record.createdAt
                    ).toLocaleString()} by ${record.uploadedBy}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => handleDeleteRecord(record.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}
    </Container>
  );
};
